import { useState, useEffect, useRef, useCallback } from "react";
import { Box, Text, Flex } from "@chakra-ui/react";
import ReusableSearchBar from "../../components/ReusableSearchBar";
import CampaignTabs from "../../components/CampaignTabs";
import PendingPaymentTab from "./PendingPaymentTab";
import RecruitingTab from "./RecruitingTab";
import OfferingTab from "./OfferingTab";
import ContractedTab from "./ContractedTab";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  pendingPaymentList,
  recuruitingList,
  offeringList,
  contractedList,
} from "../../api";

export default function SuggestPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // 탭 → API 매핑
  const apiMap = {
    pendingpayment: pendingPaymentList,
    recruiting: recuruitingList,
    offering: offeringList,
    contracted: contractedList,
  };

  // URL 기반 현재 탭 찾기
  const currentTab = Object.keys(apiMap).find((key) => pathname.includes(key));

  // 리스트 상태
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const size = 50;
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState();

  // 검색
  const [keyword, setKeyword] = useState("");

  // 무한스크롤 옵저버
  const observer = useRef();
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((p) => p + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // 공통 API 호출 함수
  const loadItems = async (targetPage = page) => {
    try {
      setLoading(true);

      const apiFn = apiMap[currentTab];
      if (!apiFn) return;

      const res = await apiFn({ page: targetPage, size });

      const pageData = res?.data?.page;
      const list = pageData?.contents ?? [];
      setCounts(res?.data?.counts);

      if (targetPage === 1) {
        setItems(list);
      } else {
        setItems((prev) => [...prev, ...list]);
      }

      setHasMore(Boolean(pageData?.hasNext));
    } catch (e) {
      console.error("loadItems error:", e);
    } finally {
      setLoading(false);
    }
  };

  // 탭 변경 시 초기화 + 첫 페이지 로드
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    setKeyword("");

    loadItems(1);
    // eslint-disable-next-line
  }, [currentTab]);

  // page 증가 시 추가 로딩
  useEffect(() => {
    if (page > 1) loadItems(page);
    // eslint-disable-next-line
  }, [page]);

  const reloadCurrentTab = () => {
    setPage(1);
    setHasMore(true);
    loadItems(1);
  };

  // 검색 필터링
  const filtered = !keyword
    ? items
    : items
        .map((item) => {
          if (!item?.campaigns && !item?.proposals) {
            const text = currentTab === "offering" ? item.proposalTitle : item.title;
            return text?.toLowerCase().includes(keyword.toLowerCase()) ? item : null;
          }

          const lowerKeyword = keyword.toLowerCase();
          const groupMatched = item.advertiserName
            ?.toLowerCase()
            .includes(lowerKeyword);
          const campaigns = (item.campaigns ?? []).filter((campaign) =>
            campaign.title?.toLowerCase().includes(lowerKeyword)
          );
          const proposals = (item.proposals ?? []).filter((proposal) =>
            [proposal.proposalTitle, proposal.campaignTitle]
              .filter(Boolean)
              .some((text) => text.toLowerCase().includes(lowerKeyword))
          );

          if (!groupMatched && campaigns.length === 0 && proposals.length === 0) {
            return null;
          }

          return {
            ...item,
            campaigns: groupMatched ? item.campaigns : campaigns,
            proposals: groupMatched ? item.proposals : proposals,
          };
        })
        .filter(Boolean);

  const campaignProps = {
    list: filtered,
    lastItemRef,
    loading,
    onChanged: reloadCurrentTab,
  };

  const tabs = [
    {
      label: `결제 대기 (${counts?.pendingpayment ?? 0})`,
      path: "pendingpayment",
    },
    { label: `모집 중 (${counts?.recruiting ?? 0})`, path: "recruiting" },
    { label: `제안 진행중 (${counts?.offering ?? 0})`, path: "offering" },
    { label: `모집 완료 (${counts?.contracted ?? 0})`, path: "contracted" },
  ];

  const title = {
    pendingpayment: "결제 대기 캠페인 목록입니다.",
    recruiting: "모집 중인 캠페인 목록입니다.",
    offering: "제안 진행중인 캠페인 목록입니다.",
    contracted: "모집 완료된 캠페인 목록입니다.",
  };
  const desc = {
    pendingpayment:
      "광고주가 결제를 완료하기를 기다리는 캠페인입니다. 결제 완료 후 모집 단계로 이동합니다.",
    recruiting:
      "크리에이터를 모집 중인 캠페인입니다. 지원자를 검토하고 제안을 진행할 수 있습니다.",
    offering:
      "광고주에게 제안을 보내고 협의 중인 캠페인입니다. 제안 수락 시 계약이 완료되어 콘텐츠 제작으로 넘어갑니다.",
    contracted:
      "모집 기간이 만료된 캠페인 목록입니다.",
  };

  const activeIndex = tabs.findIndex((t) => pathname.includes(t.path));

  const onTabChange = (index) => {
    navigate(`/suggest/${tabs[index].path}`);
  };

  return (
    <Flex flexDir="column" h="100%" overflow="hidden">
      {/* HEADER */}
      <Flex justify="space-between" alignItems="center" mb="24px">
        <CampaignTabs
          tabLabels={tabs.map((t) => t.label)}
          onChange={onTabChange}
          activeIndex={activeIndex}
        />
        <ReusableSearchBar
          placeholder="캠페인 검색"
          width="229px"
          onSearch={(v) => setKeyword(v)}
        />
      </Flex>

      {/* TITLE */}
      <Box mb="16px">
        <Text fontSize="xl" fontWeight="bold" mb="4px">
          {title[currentTab]}
        </Text>
        <Text color="gray.600">{desc[currentTab] ?? desc.pendingpayment}</Text>
      </Box>

      {/* CONTENT */}
      <Box flex="1" overflowY="auto">
        <Routes>
          <Route
            path="pendingpayment/*"
            element={<PendingPaymentTab {...campaignProps} />}
          />
          <Route
            path="recruiting/*"
            element={<RecruitingTab {...campaignProps} />}
          />
          <Route
            path="offering/*"
            element={<OfferingTab {...campaignProps} />}
          />
          <Route
            path="contracted/*"
            element={<ContractedTab {...campaignProps} />}
          />

          <Route path="*" element={<Navigate to="pendingpayment" replace />} />
        </Routes>
      </Box>
    </Flex>
  );
}
